import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  message,
  Select,
  Space,
  Spin,
  Table,
  Tag,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  FilterOutlined,
  MailOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { serverFetch } from "../../server";
import { fmtDate, strDate2Server } from "../../extensions/convert";
import { renderCheckbox } from "../../extensions/helper";
import { progKeysStatusRender } from "../../extensions/enums";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "../company/company.css";
import "../orders/order.css";
import "./license-keys.css";
import LicenseOrderKey from "./license-order-key";

const vidPerOptions = [
  { value: 0, label: "Все" },
  { value: 1, label: "Период активации" },
  { value: 2, label: "Период окончания" },
];

const statusTagColor = s => {
  const map = { 0: "default", 1: "blue", 2: "green", 3: "cyan", 4: "orange", 5: "red" };
  return map[s] ?? "default";
};

const LicenseKeys = () => {
  const isMobile = useViewportBreakpoint() === "mobile";
  const [filter, setFilter] = useState({
    dateBeg: dayjs(),
    dateEnd: dayjs(),
    vidPer: 0,
    searchText: "",
    allCompanies: false,
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const user = useSelector(s => s.user);

  const [filterOpen, setFilterOpen] = useState(false);
  const [draftVidPer, setDraftVidPer] = useState(0);
  const [draftDateBeg, setDraftDateBeg] = useState(null);
  const [draftDateEnd, setDraftDateEnd] = useState(null);
  const [draftAllCompanies, setDraftAllCompanies] = useState(false);

  const loadSortSettings = () => {
    try {
      const saved = localStorage.getItem("licenseKeys_sort");
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          sortField: parsed.sortField || null,
          sortOrder: parsed.sortOrder || null,
        };
      }
    } catch (e) {
      console.error("Error loading sort settings from localStorage:", e);
    }
    return { sortField: null, sortOrder: null };
  };

  const [sortSettings, setSortSettings] = useState(loadSortSettings);

  const saveSortSettings = (field, order) => {
    try {
      const toSave = { sortField: field, sortOrder: order };
      localStorage.setItem("licenseKeys_sort", JSON.stringify(toSave));
      setSortSettings(toSave);
    } catch (e) {
      console.error("Error saving sort settings to localStorage:", e);
    }
  };

  const fetchKeys = f => {
    setLoading(true);

    let url = `license/key`;
    let hasParams = false;

    if (f.vidPer > 0) {
      url += `?vidPer=${f.vidPer}&dateBeg=${strDate2Server(f.dateBeg)}&dateEnd=${strDate2Server(f.dateEnd)}`;
      hasParams = true;
    }

    if (user?.admin && !f.allCompanies && user?.companyId) {
      const separator = hasParams ? "&" : "?";
      url += `${separator}companyId=${user.companyId}`;
      hasParams = true;
    }

    if (f.searchText.trim()) {
      const separator = hasParams ? "&" : "?";
      url += `${separator}searchText=${encodeURIComponent(f.searchText.trim())}`;
    }

    serverFetch(url)
      .then(data => {
        setLoading(false);
        setItems(data.map(i => ({ ...i, keyId: `${i.companyId || ""}/${i.accId || ""}/${i.id || ""}` })));
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const getData = () => fetchKeys(filter);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- первичная загрузка
  }, []);

  const filterSummary = useMemo(() => {
    const parts = [];
    parts.push(vidPerOptions.find(o => o.value === filter.vidPer)?.label ?? "Все");
    if (filter.vidPer > 0 && filter.dateBeg && filter.dateEnd) {
      parts.push(`${filter.dateBeg.format("DD.MM.YYYY")} — ${filter.dateEnd.format("DD.MM.YYYY")}`);
    }
    if (user?.admin) {
      parts.push(filter.allCompanies ? "все компании" : "только своя компания");
    }
    const q = filter.searchText.trim();
    if (q) parts.push(`поиск: ${q.length > 40 ? `${q.slice(0, 40)}…` : q}`);
    return parts.join(" · ");
  }, [filter, user?.admin]);

  const openFilterModal = () => {
    setDraftVidPer(filter.vidPer);
    setDraftDateBeg(filter.dateBeg);
    setDraftDateEnd(filter.dateEnd);
    setDraftAllCompanies(filter.allCompanies);
    setFilterOpen(true);
  };

  const applyFilter = () => {
    if (draftVidPer > 0 && (!draftDateBeg || !draftDateEnd)) {
      message.warning("Укажите период дат");
      return;
    }
    const next = {
      ...filter,
      vidPer: draftVidPer,
      dateBeg: draftDateBeg,
      dateEnd: draftDateEnd,
      ...(user?.admin ? { allCompanies: draftAllCompanies } : {}),
    };
    setFilter(next);
    setFilterOpen(false);
    fetchKeys(next);
  };

  const cancelFilter = () => setFilterOpen(false);

  const stringSorter = (a, b, dataIndex) => {
    const aVal = a[dataIndex] || "";
    const bVal = b[dataIndex] || "";
    return aVal.localeCompare(bVal, "ru", { numeric: true, sensitivity: "base" });
  };

  const dateSorter = (a, b, dataIndex) => {
    const aVal = a[dataIndex] ? new Date(a[dataIndex]).getTime() : 0;
    const bVal = b[dataIndex] ? new Date(b[dataIndex]).getTime() : 0;
    return aVal - bVal;
  };

  const numberSorter = (a, b, dataIndex) => {
    const aVal = a[dataIndex] ?? 0;
    const bVal = b[dataIndex] ?? 0;
    return aVal - bVal;
  };

  const booleanSorter = (a, b, dataIndex) => {
    const aVal = a[dataIndex] ? 1 : 0;
    const bVal = b[dataIndex] ? 1 : 0;
    return aVal - bVal;
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        dataIndex: "companyId",
        title: "#",
        width: 120,
        render: (text, record) => `${record.companyId || ""}/${record.accId || ""}/${record.id || ""}`,
        sorter: (a, b) => {
          const aVal = `${a.companyId || ""}/${a.accId || ""}/${a.id || ""}`;
          const bVal = `${b.companyId || ""}/${b.accId || ""}/${b.id || ""}`;
          return aVal.localeCompare(bVal, "ru", { numeric: true });
        },
        sortOrder: sortSettings.sortField === "companyId" ? sortSettings.sortOrder : null,
      },
      ...(user?.admin
        ? [
            {
              dataIndex: "companyName",
              title: "Компания",
              width: 200,
              sorter: (a, b) => stringSorter(a, b, "companyName"),
              sortOrder: sortSettings.sortField === "companyName" ? sortSettings.sortOrder : null,
            },
          ]
        : []),
      {
        dataIndex: "docDate",
        title: "Дата",
        render: fmtDate,
        sorter: (a, b) => dateSorter(a, b, "docDate"),
        sortOrder: sortSettings.sortField === "docDate" ? sortSettings.sortOrder : null,
      },
      {
        dataIndex: "progName",
        title: "Программа",
        sorter: (a, b) => stringSorter(a, b, "progName"),
        sortOrder: sortSettings.sortField === "progName" ? sortSettings.sortOrder : null,
      },
      {
        dataIndex: "licName",
        title: "Лицензия",
        sorter: (a, b) => stringSorter(a, b, "licName"),
        sortOrder: sortSettings.sortField === "licName" ? sortSettings.sortOrder : null,
      },
      {
        dataIndex: "status",
        title: "Статус",
        render: progKeysStatusRender,
        sorter: (a, b) => numberSorter(a, b, "status"),
        sortOrder: sortSettings.sortField === "status" ? sortSettings.sortOrder : null,
      },
      {
        dataIndex: "startDate",
        title: "Актив.",
        render: fmtDate,
        sorter: (a, b) => dateSorter(a, b, "startDate"),
        sortOrder: sortSettings.sortField === "startDate" ? sortSettings.sortOrder : null,
      },
      {
        dataIndex: "finDate",
        title: "Истек.",
        render: fmtDate,
        sorter: (a, b) => dateSorter(a, b, "finDate"),
        sortOrder: sortSettings.sortField === "finDate" ? sortSettings.sortOrder : null,
      },
      {
        dataIndex: "comment",
        title: "Комментарий",
        sorter: (a, b) => stringSorter(a, b, "comment"),
        sortOrder: sortSettings.sortField === "comment" ? sortSettings.sortOrder : null,
      },
      {
        dataIndex: "workGuid",
        title: "Рабочая станция",
        sorter: (a, b) => stringSorter(a, b, "workGuid"),
        sortOrder: sortSettings.sortField === "workGuid" ? sortSettings.sortOrder : null,
      },
      {
        dataIndex: "frozen",
        title: "Заморожен",
        width: 100,
        minWidth: 100,
        render: renderCheckbox,
        sorter: (a, b) => booleanSorter(a, b, "frozen"),
        sortOrder: sortSettings.sortField === "frozen" ? sortSettings.sortOrder : null,
      },
      {
        dataIndex: "sent",
        title: "Отправлен",
        width: 100,
        minWidth: 100,
        render: renderCheckbox,
        sorter: (a, b) => booleanSorter(a, b, "sent"),
        sortOrder: sortSettings.sortField === "sent" ? sortSettings.sortOrder : null,
      },
    ];
    return baseColumns;
  }, [sortSettings, user?.admin]);

  const onFilterChange = value => setFilter({ ...filter, ...value });

  const handleKeyPress = e => {
    if (e.key === "Enter") {
      getData();
    }
  };

  const handleSearchKeyDown = e => {
    if (e.key === "Enter") {
      getData();
    }
  };

  const onRowClick = record => {
    setSelectedKey({
      companyId: record.companyId,
      accId: record.accId,
      keyId: record.id,
    });
  };

  const handleKeyModalClose = refresh => {
    setSelectedKey(null);
    refresh && getData();
  };

  const idLine = record =>
    `${record.companyId || ""}/${record.accId || ""}/${record.id || ""}`;

  const trimPart = v => (v != null && String(v).trim() !== "" ? String(v).trim() : null);

  /** Компания, программа, лицензия, рабочая станция, комментарий — через запятую, пустые не выводим */
  const buildDetailLine = record => {
    const parts = [
      trimPart(record.companyName),
      trimPart(record.progName),
      trimPart(record.licName),
      trimPart(record.workGuid),
      trimPart(record.comment),
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : "";
  };

  const mobileListEl = (
    <Spin spinning={loading}>
      <div className="license-keys-mobile-list">
        {items.map(record => {
          const keyId = record.keyId;
          const statusLabel = progKeysStatusRender(record.status) ?? "—";
          const detailLine = buildDetailLine(record);

          return (
            <div
              key={keyId}
              className={`license-keys-mobile-card${record.sent ? " license-keys-mobile-card--sent" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => onRowClick(record)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onRowClick(record);
                }
              }}
            >
              {record.sent ? (
                <span className="license-keys-mobile-mail-corner" title="Отправлен" aria-label="Отправлен">
                  <MailOutlined />
                </span>
              ) : null}
              <div className="license-keys-mobile-row1">
                <div className="license-keys-mobile-row1-main">
                  <Tag color={statusTagColor(record.status)}>{statusLabel}</Tag>
                  <span className="license-keys-mobile-id">#{idLine(record)}</span>
                </div>
              </div>
              <div className="license-keys-mobile-row2">
                <span className="license-keys-mobile-row2-dates">
                  {fmtDate(record.startDate) || "—"} <span className="license-keys-mobile-date-sep">→</span>{" "}
                  {fmtDate(record.finDate) || "—"}
                </span>
                {!detailLine && record.frozen ? (
                  <span className="license-keys-mobile-comment-icons">
                    <span className="license-keys-mobile-frozen-icon" title="Заморожен" aria-label="Заморожен">
                      &#10052;
                    </span>
                  </span>
                ) : null}
              </div>
              {detailLine ? (
                <div className="license-keys-mobile-row3">
                  <span className="license-keys-mobile-comment">{detailLine}</span>
                  {record.frozen ? (
                    <span className="license-keys-mobile-comment-icons">
                      <span className="license-keys-mobile-frozen-icon" title="Заморожен" aria-label="Заморожен">
                        &#10052;
                      </span>
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Spin>
  );

  const tableEl = (
    <Table
      className="license-keys-table"
      columns={columns}
      dataSource={items}
      loading={loading}
      rowKey="keyId"
      pagination={false}
      size="small"
      sortDirections={["ascend", "descend", "ascend"]}
      scroll={{ x: "max-content", y: "calc(100vh - 240px)" }}
      onChange={(pagination, filters, sorter) => {
        if (sorter.field) {
          saveSortSettings(sorter.field, sorter.order);
        } else {
          saveSortSettings(null, null);
        }
      }}
      onRow={record => ({
        onClick: () => onRowClick(record),
        style: { cursor: "pointer" },
      })}
    />
  );

  return (
    <div
      className={
        isMobile ? "page orders-page--mobile license-keys-page--mobile" : "page license-keys-page--desktop"
      }
    >
      {isMobile ? (
        <>
          <div className="license-keys-mobile-body">
            <div className="orders-mobile-toolbar">
              <div className="company-backups-mobile-search-row">
                <Input
                  placeholder="Поиск по компании, ИНН, телефону, email, ключу, GUID, пользователю"
                  value={filter.searchText}
                  onChange={e => setFilter({ ...filter, searchText: e.target.value })}
                  onKeyDown={handleSearchKeyDown}
                  allowClear
                  className="company-backups-mobile-search-input"
                />
              </div>
              <div className="company-backups-mobile-filter-row">
                <span className="company-backups-mobile-filter-summary" title={filterSummary}>
                  {filterSummary}
                </span>
                <Button
                  type="default"
                  icon={<FilterOutlined />}
                  onClick={openFilterModal}
                  aria-label="Фильтр"
                  className="company-backups-mobile-filter-icon-btn"
                />
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  onClick={getData}
                  loading={loading}
                  disabled={filter.vidPer > 0 && (!filter.dateBeg || !filter.dateEnd)}
                  aria-label="Обновить"
                  className="company-backups-mobile-refresh-btn"
                />
              </div>
            </div>
            <div className="orders-mobile-scroll license-keys-mobile-scroll">{mobileListEl}</div>
          </div>
          {filterOpen && (
            <div
              className="misoft-modal-overlay company-backups-filter-overlay--top"
              onClick={e => e.target === e.currentTarget && cancelFilter()}
            >
              <div
                className="misoft-modal-content company-backups-filter-sheet--top"
                onClick={e => e.stopPropagation()}
              >
                <div className="company-base-sheet-header">
                  <span className="company-base-sheet-title">Фильтр</span>
                  <Space size="small" className="company-base-sheet-actions">
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={applyFilter}
                      aria-label="Применить"
                      className="company-base-header-icon-btn"
                    />
                    <Button
                      type="default"
                      icon={<CloseOutlined />}
                      onClick={cancelFilter}
                      aria-label="Отмена"
                      className="company-base-header-icon-btn"
                    />
                  </Space>
                </div>
                <div className="company-base-sheet-body company-backups-filter-sheet-body">
                  <div className="company-backups-filter-field">
                    <span className="company-backups-filter-label">Период</span>
                    <Select
                      style={{ width: "100%" }}
                      options={vidPerOptions}
                      value={draftVidPer}
                      onChange={setDraftVidPer}
                    />
                  </div>
                  {draftVidPer > 0 ? (
                    <>
                      <div className="company-backups-filter-field">
                        <span className="company-backups-filter-label">С даты</span>
                        <DatePicker
                          value={draftDateBeg}
                          onChange={setDraftDateBeg}
                          format="DD.MM.YYYY"
                          allowClear={false}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div className="company-backups-filter-field">
                        <span className="company-backups-filter-label">По дату</span>
                        <DatePicker
                          value={draftDateEnd}
                          onChange={setDraftDateEnd}
                          format="DD.MM.YYYY"
                          allowClear={false}
                          style={{ width: "100%" }}
                        />
                      </div>
                    </>
                  ) : null}
                  {user?.admin ? (
                    <div className="company-backups-filter-field company-backups-filter-switch-row">
                      <span>По всем компаниям</span>
                      <Checkbox checked={draftAllCompanies} onChange={e => setDraftAllCompanies(e.target.checked)} />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <Space style={{ flexShrink: 0, marginBottom: 16 }} wrap>
            <Select
              options={vidPerOptions}
              value={filter.vidPer}
              onChange={e => onFilterChange({ vidPer: e })}
              style={{ width: 200 }}
            />
            {filter.vidPer > 0 && (
              <>
                <div>Период с</div>
                <DatePicker
                  format="DD.MM.YYYY"
                  value={filter.dateBeg}
                  onChange={e => onFilterChange({ dateBeg: e })}
                  allowClear={false}
                />
                <div>по</div>
                <DatePicker
                  format="DD.MM.YYYY"
                  value={filter.dateEnd}
                  onChange={e => onFilterChange({ dateEnd: e })}
                  allowClear={false}
                />
              </>
            )}
            <Input
              placeholder="Поиск по компании, ИНН, телефону, email, ключу, GUID, пользователю"
              value={filter.searchText}
              onChange={e => setFilter({ ...filter, searchText: e.target.value })}
              onKeyPress={handleKeyPress}
              style={{ width: 400 }}
            />
            {user?.admin && (
              <Checkbox
                checked={filter.allCompanies}
                onChange={e => setFilter({ ...filter, allCompanies: e.target.checked })}
              >
                По всем компаниям
              </Checkbox>
            )}
            <Button type="primary" onClick={getData} loading={loading}>
              Обновить
            </Button>
          </Space>
          <div className="license-keys-desktop-scroll">{tableEl}</div>
        </>
      )}

      {selectedKey && (
        <LicenseOrderKey
          companyId={selectedKey.companyId}
          accId={selectedKey.accId}
          keyId={selectedKey.keyId}
          onClose={handleKeyModalClose}
        />
      )}
    </div>
  );
};

export default LicenseKeys;
